// Snapping for the free-placement nest canvas (2026-10-01).
//
// Trimmed copy of printEditor/studioCore/lib/aligningGuidelines.js (itself from
// the fabric.js aligning_guidelines demo, MIT). Copied rather than imported so
// the editor module — mirrored into studioCloud by path — stays untouched, and
// so the snap targets are ONLY the paper ("clip") and other tiles (the roll
// band around the paper must never be a target: snapping to it would put a
// tile off the printable strip). Added: a gutter snap, so a tile can sit
// exactly NEST_GAP_IN beside or below a neighbour (the cut allowance).
//
// While a tile moves, its left / centre / right and top / centre / bottom are
// compared with each target; the nearest candidate within `margin` screen px
// wins per axis, the tile snaps and a guide line is drawn on contextTop (never
// part of an export render — see isOnScreenRender).

import { Point } from 'fabric';

function isOnScreenRender(canvas, e) {
  const ctx = e?.ctx;
  if (!ctx) return true;
  return ctx === canvas.contextContainer || ctx === canvas.contextTop;
}

export function initNestSnapping(canvas, { margin = 8, color = 'rgb(236, 72, 153)', gap = 0.25, isTarget } = {}) {
  let lines = [];   // { x1, y1, x2, y2 } scene coords

  const targets = (active) => canvas.getObjects().filter((o) => o !== active && o.visible !== false && isTarget(o));

  function onMoving(e) {
    const a = e.target;
    if (!a || !e.transform) return;
    const zoom = canvas.getZoom();
    const tol = margin / zoom;
    const br = a.getBoundingRect();                 // scene coords (v6)
    const ax = [br.left, br.left + br.width / 2, br.left + br.width];
    const ay = [br.top, br.top + br.height / 2, br.top + br.height];
    let bestX = null, bestY = null;
    for (const o of targets(a)) {
      const ob = o.getBoundingRect();
      const tile = o.name !== 'clip';
      const ox = [ob.left, ob.left + ob.width / 2, ob.left + ob.width];
      const oy = [ob.top, ob.top + ob.height / 2, ob.top + ob.height];
      // x: edges/centres line up, or (tiles) a gutter apart side by side
      const xc = [];
      for (let i = 0; i < 3; i++) for (let j = 0; j < 3; j++) xc.push({ d: ox[j] - ax[i], at: ox[j] });
      if (tile) { xc.push({ d: ox[2] + gap - ax[0], at: ox[2] + gap }); xc.push({ d: ox[0] - gap - ax[2], at: ox[0] - gap }); }
      for (const c of xc) if (Math.abs(c.d) <= tol && (!bestX || Math.abs(c.d) < Math.abs(bestX.d))) bestX = { ...c, y1: Math.min(ob.top, br.top), y2: Math.max(ob.top + ob.height, br.top + br.height) };
      const yc = [];
      for (let i = 0; i < 3; i++) for (let j = 0; j < 3; j++) yc.push({ d: oy[j] - ay[i], at: oy[j] });
      if (tile) { yc.push({ d: oy[2] + gap - ay[0], at: oy[2] + gap }); yc.push({ d: oy[0] - gap - ay[2], at: oy[0] - gap }); }
      for (const c of yc) if (Math.abs(c.d) <= tol && (!bestY || Math.abs(c.d) < Math.abs(bestY.d))) bestY = { ...c, x1: Math.min(ob.left, br.left), x2: Math.max(ob.left + ob.width, br.left + br.width) };
    }
    lines = [];
    if (bestX || bestY) {
      const c = a.getCenterPoint();
      a.setPositionByOrigin(new Point(c.x + (bestX ? bestX.d : 0), c.y + (bestY ? bestY.d : 0)), 'center', 'center');
      a.setCoords();
      if (bestX) lines.push({ x1: bestX.at, y1: bestX.y1, x2: bestX.at, y2: bestX.y2 });
      if (bestY) lines.push({ x1: bestY.x1, y1: bestY.at, x2: bestY.x2, y2: bestY.at });
    }
  }

  function onAfterRender(e) {
    if (!isOnScreenRender(canvas, e) || !lines.length) return;
    const ctx = canvas.contextTop;
    if (!ctx) return;
    const v = canvas.viewportTransform;
    ctx.save();
    ctx.lineWidth = 1;
    ctx.strokeStyle = color;
    ctx.beginPath();
    for (const l of lines) {
      ctx.moveTo(l.x1 * v[0] + v[4] + 0.5, l.y1 * v[3] + v[5] + 0.5);
      ctx.lineTo(l.x2 * v[0] + v[4] + 0.5, l.y2 * v[3] + v[5] + 0.5);
    }
    ctx.stroke();
    ctx.restore();
  }

  function onUp() { lines = []; }

  canvas.on('object:moving', onMoving);
  canvas.on('after:render', onAfterRender);
  canvas.on('mouse:up', onUp);
  return () => {
    canvas.off('object:moving', onMoving);
    canvas.off('after:render', onAfterRender);
    canvas.off('mouse:up', onUp);
  };
}
